import { AerCheckboxGroup } from "@aerstudios/aerated-aercheckboxgroup";
import { AerInputStructure } from "@aerstudios/aerated-aerinputstructure";
import cx from "classnames";
import React, {
	type MouseEventHandler,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import Check from "../../common/Icons/Check";
import * as styles from "./CheckboxGroup.module.scss";

export interface CheckboxGroupProps {
	// The id of the checkbox group
	id: string;
	// The label for the checkbox group
	groupLabel: string | { text: string; hide: boolean };
	// The maximum number of checkboxes that can be selected
	maxSelections?: number;
	// The options for the checkbox group
	options: NDS.FormOption[];
	// The value of the selected checkboxes
	value: string[];
	// The function to call when a checkbox is selected
	onChange: (value: string[]) => void;
	// The error message to display
	errorMessage?: string;
	// Whether the element is hidden
	hidden?: boolean;
	// Whether the group is a group using images
	isImageGroup?: boolean;

	// If the group is disabled
	disabled?: boolean;

	// If the answer is exclusive
	exclusiveAnswer?: number | number[];
}
export const NDSCheckboxGroup: React.FC<CheckboxGroupProps> = ({
	id,
	groupLabel,
	maxSelections,
	onChange,
	errorMessage,
	hidden,
	isImageGroup,
	disabled,
	exclusiveAnswer = -1,
	options = [],
	value = [],
}) => {
	// Checkbox Group
	// Is multi-select
	// Has a label
	// Label can have an additional, non bolded text
	// Can be limited to a certain number of selections

	// Checkbox image group
	// Is multi-select
	// Has a label which is visually hidden
	// Can be limited to a certain number of selections

	const [shouldRefocus, setShouldRefocus] = useState<false | string>(false);

	const handleChange = useCallback(
		(v: string): MouseEventHandler<HTMLInputElement> =>
			(e) => {
				const { currentTarget } = e;
				const prevSet = new Set(value);
				const verb = currentTarget.checked ? "add" : "delete";

				prevSet[verb](v);

				const nextState = [...prevSet];

				setShouldRefocus(v);
				onChange(nextState);
			},
		[onChange, value],
	);

	useEffect(() => {
		if (shouldRefocus) {
			const element = document.getElementById(`${id}-${shouldRefocus}`);
			if (element) {
				element.focus();
			}
			setShouldRefocus(false);
		}
	}, [shouldRefocus, id]);

	const exclusiveIsChecked = Array.isArray(exclusiveAnswer)
		? exclusiveAnswer.some((index) => value.includes(options[index]?.value))
		: exclusiveAnswer >= 0 && value.includes(options[exclusiveAnswer]?.value);

	const nonExclusiveIsChecked = options.some((option, i) => {
		if (Array.isArray(exclusiveAnswer)) {
			return !exclusiveAnswer.includes(i) && value.includes(option.value);
		}
		return exclusiveAnswer !== i && value.includes(option.value);
	});

	const checkboxGroupRef = useRef<HTMLFieldSetElement>(null);

	return (
		<AerInputStructure.Root
			className={styles.checkboxGroupWrapper}
			errorMessage={errorMessage}
		>
			<AerCheckboxGroup.Root
				id={id}
				ref={checkboxGroupRef}
				groupLabel={groupLabel}
				groupLabelClassName={styles.checkboxGroupLabel}
				className={cx(styles.checkboxGroup, {
					[styles.groupWithImages]: isImageGroup,
				})}
				value={value}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
					}
				}}
				hidden={hidden}
			>
				{options?.map((answer, i) => {
					const isChecked = value.includes(answer.value);

					const isExclusive = Array.isArray(exclusiveAnswer)
						? exclusiveAnswer.includes(i)
						: exclusiveAnswer === i;

					const maxSelectionsAndNotChecked =
						maxSelections && value.length >= maxSelections && !isChecked;

					const notExclusiveAndExclusiveIsChecked =
						!isExclusive && exclusiveIsChecked;

					const isExclusiveAndAnotherExclusiveIsChecked =
						isExclusive && exclusiveIsChecked && !isChecked;

					const isExclusiveAndANonExclusiveIsChecked =
						isExclusive && nonExclusiveIsChecked && !isChecked;

					const isReadOnly =
						disabled ||
						maxSelectionsAndNotChecked ||
						notExclusiveAndExclusiveIsChecked ||
						isExclusiveAndAnotherExclusiveIsChecked ||
						isExclusiveAndANonExclusiveIsChecked;

					const checkBoxContent = (
						<img
							className={styles.checkboximage}
							src={answer.imgSrc}
							alt={""}
							aria-hidden="true"
						/>
					);
					const hasImage = answer.imgSrc;
					const labelContent = hasImage ? (
						checkBoxContent
					) : (
						<span
							// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
							dangerouslySetInnerHTML={{ __html: answer.label }}
							className={styles.labelContent}
						/>
					);

					return (
						<AerCheckboxGroup.Item
							id={`${id}-${answer.value}`}
							key={answer.value}
							label={labelContent}
							value={answer.value}
							checkBox={<span className={styles.checkbox} />}
							checkedIcon={
								<span className={styles.checkedIcon}>
									<Check
										height="12px"
										width="14px"
										className={styles.checkboxTick}
										aria-hidden="true"
										aria-label="checkbox tick"
									/>
								</span>
							}
							readOnly={isReadOnly && !isChecked}
							disabled={isReadOnly || disabled}
							checked={isChecked}
							className={cx(
								styles.checkboxItem,
								{
									[styles.firstItem]: i === 0,
								},
								{ [styles.withImage]: hasImage },
							)}
							onClick={handleChange(answer.value)}
						/>
					);
				})}
			</AerCheckboxGroup.Root>
			<AerInputStructure.ErrorMessage />
		</AerInputStructure.Root>
	);
};
